import React from "react";
import { ActionButton } from "halifax";
import { Prices, DelayId, MissedConnectionId, CfarId, ChfarId } from "redmond";
import clsx from "clsx";
import type { Prices as ChfarPrices } from "@b2bportal/air-chfar-api";
import { CfarAttachedContent, constants as CfarConstants } from "../../cfar";
import {
  ChfarAttachedContent,
  DECLINED_COLLAPSED_COPY as CHFAR_DECLINED_COLLAPSED_COPY,
} from "../../chfar";
import { constants as DisruptionConstants } from "../../disruptionProtection";
import "../styles.scss";
import { CardWrapper } from "../../AddOnCardWrapper";

import { CollapsedContent } from "../../CollapsedContent";
import {
  DO_NOT_APPLY_CFAR_OPTION,
  DO_NOT_APPLY_CHFAR_OPTION,
} from "../../helpers";
import {
  ISetSelectedCfarId,
  ISetSelectedChfarId,
} from "../../../../actions/actions";

type GroupedAncillariesProps = {
  isDisruptionProtectionAvailable: boolean;
  isDisruptionProtectionCollapsed: boolean;
  setIsDisruptionProtectionCollapsed: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  renderDisruptionProtectionAddOnItem: () => React.JSX.Element;
  disruptionProtectionOfferPrices: Prices | undefined;
  isCfarAvailable: boolean;
  isChfarAvailable: boolean;
  isCfarCollapsed: boolean;
  isChfarCollapsed: boolean;
  setIsCfarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setIsChfarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDisruptionProtectionId: MissedConnectionId | DelayId | null;
  renderCfarAddOnItem: () => React.JSX.Element;
  cfarOfferPrices: Prices | undefined;
  rewardsKey: string | undefined;
  renderChfarAddOnItem: () => React.JSX.Element;
  chfarOfferPrices: ChfarPrices | undefined;
  isChfarVariantDeclineAll: boolean;
  setSelectedCfarId: (cfarId: CfarId | null) => ISetSelectedCfarId;
  setSelectedChfarId: (chfarId: ChfarId | null) => ISetSelectedChfarId;
};

export const GroupedAncillaries: React.FC<GroupedAncillariesProps> = ({
  isDisruptionProtectionAvailable,
  isDisruptionProtectionCollapsed,
  setIsDisruptionProtectionCollapsed,
  renderDisruptionProtectionAddOnItem,
  disruptionProtectionOfferPrices,
  isCfarAvailable,
  isChfarAvailable,
  isCfarCollapsed,
  isChfarCollapsed,
  setIsCfarCollapsed,
  setIsChfarCollapsed,
  selectedDisruptionProtectionId,
  renderCfarAddOnItem,
  cfarOfferPrices,
  rewardsKey,
  renderChfarAddOnItem,
  chfarOfferPrices,
  isChfarVariantDeclineAll,
  setSelectedCfarId,
  setSelectedChfarId,
}) => (
  <>
    {isDisruptionProtectionAvailable && (
      <CardWrapper
        title="1. In-transit assistance"
        displayEdit={isDisruptionProtectionCollapsed}
        onEdit={() => {
          setIsDisruptionProtectionCollapsed(false);
        }}
        disabled={false}
      >
        {!isDisruptionProtectionCollapsed ? (
          renderDisruptionProtectionAddOnItem()
        ) : (
          <CollapsedContent
            attached={!!disruptionProtectionOfferPrices}
            attachedContent={<span>{DisruptionConstants.ATTACHED_COPY}</span>}
            declinedContent={<span>{DisruptionConstants.DECLINED_COPY}</span>}
          />
        )}
      </CardWrapper>
    )}
    {(isCfarAvailable || isChfarAvailable) && (
      <CardWrapper
        title="2. Extra flexibility"
        displayEdit={isCfarCollapsed || isChfarCollapsed}
        onEdit={() => {
          setIsCfarCollapsed(false);
          setIsChfarCollapsed(false);
        }}
        disabled={
          isDisruptionProtectionAvailable
            ? !selectedDisruptionProtectionId
            : false
        }
        subtitle="Add flexibility to cancel or change flights when travel doesn't go as planned."
      >
        {isCfarAvailable && !isCfarCollapsed && renderCfarAddOnItem()}
        {isCfarAvailable && isCfarCollapsed && (
          <div
            className={clsx("collapsed-content-container", {
              isNotLastItem: true,
            })}
          >
            <CollapsedContent
              attached={!!cfarOfferPrices}
              attachedContent={
                <CfarAttachedContent
                  cfarOfferPrices={cfarOfferPrices}
                  rewardsKey={rewardsKey}
                />
              }
              declinedContent={<span>{CfarConstants.DECLINED_COPY}</span>}
            />
          </div>
        )}
        {isChfarAvailable && !isChfarCollapsed && renderChfarAddOnItem()}
        {isChfarAvailable && isChfarCollapsed && (
          <div className="collapsed-content-container">
            <CollapsedContent
              attached={!!chfarOfferPrices}
              attachedContent={
                <ChfarAttachedContent
                  chfarOfferPrices={chfarOfferPrices}
                  rewardsKey={rewardsKey}
                />
              }
              declinedContent={<span>{CHFAR_DECLINED_COLLAPSED_COPY}</span>}
            />
          </div>
        )}
        {isChfarVariantDeclineAll && (!isCfarCollapsed || !isChfarCollapsed) ? (
          <div className="decline-all-button-row">
            <ActionButton
              defaultStyle="h4r-primary"
              message="Decline extra flexibility options"
              onClick={() => {
                setSelectedCfarId(DO_NOT_APPLY_CFAR_OPTION);
                setIsCfarCollapsed(true);
                setSelectedChfarId(DO_NOT_APPLY_CHFAR_OPTION);
                setIsChfarCollapsed(true);
              }}
            />
          </div>
        ) : null}
      </CardWrapper>
    )}
  </>
);
